import React, { useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Tabs } from "antd";
import { CheckOutlined, DownOutlined, FilterOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import styled from "styled-components";
import ResponsiveModal from "@components/ResponsiveModal";
import FacilityBenchmark from "@/containers/facility/Benchmark/FacilityBenchmarkInner/FacilityBenchmark";
import AllFacilityBenchmark from "@/containers/facility/Benchmark/AllFacilityBenchmark";
import NationalBenchmark from "@containers/facility/Benchmark/NationalBenchmark/NationalBenchmark";
import { buildFacilityPath } from "@/utils/route-context";

const { TabPane } = Tabs;

const DEFAULT_TAB = "inter-company";
const TAB_KEYS = ["inter-company", "all-facility", "national"];
const TAB_OPTIONS = [
  {
    key: "inter-company",
    label: "Inter-company Benchmark",
    render: (props = {}) => <FacilityBenchmark {...props} />,
    supportsFilter: true,
  },
  {
    key: "all-facility",
    label: "All Facility Benchmark",
    render: (props = {}) => <AllFacilityBenchmark {...props} />,
    supportsFilter: true,
  },
  {
    key: "national",
    label: "National Benchmark",
    render: () => <NationalBenchmark />,
    supportsFilter: false,
  },
];

const BenchmarkWrapper = styled.div`
  width: 100%;
  padding: 16px;

  .benchmark-tabs {
    width: 100%;
  }

  .benchmark-tabs > .ant-tabs-nav {
    margin-bottom: 12px;
  }

  .benchmark-tabs .ant-tabs-tab {
    padding: 10px 0;
  }

  .benchmark-tabs .ant-tabs-content-holder {
    min-width: 0;
  }

  @media (max-width: 767px) {
    padding: 8px 0;

    .benchmark-mobile-switch {
      padding: 0 8px 8px;
    }

    .benchmark-mobile-switch-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .benchmark-mobile-switch-button {
      min-width: 0;
      flex: 1;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-radius: 8px;
      background: #ffffff;
      border-color: #d9e1ec;
      color: #1f2937;
      font-weight: 500;
    }

    .benchmark-mobile-filter-button {
      flex: 0 0 auto;
      height: 40px;
      border-radius: 8px;
      border-color: #4090ff;
      color: #1665d8;
      font-weight: 600;
      background: #ffffff;
    }

    .benchmark-tabs > .ant-tabs-nav {
      margin: 0 8px 8px;
    }

    .benchmark-tabs .ant-tabs-tab {
      padding: 8px 0;
      font-size: 13px;
    }

    .benchmark-tabs .ant-tabs-tab + .ant-tabs-tab {
      margin-left: 18px;
    }
  }
`;

const ViewPickerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .benchmark-view-option {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #1f2937;
    font-weight: 500;
  }

  .benchmark-view-option.active {
    border-color: #4090ff;
    background: #eef6ff;
    color: #1665d8;
  }
`;

const getValidTab = (tab) => (TAB_KEYS.includes(tab) ? tab : DEFAULT_TAB);

export const BenchmarkLegacyRedirect = ({ tab }) => {
  const { companyId, facilityId } = useParams();
  const targetPath = buildFacilityPath("benchmark", {
    companyId,
    facilityId,
    query: { tab: getValidTab(tab) },
  });

  return <Navigate to={targetPath || "/switch-view"} replace />;
};

const Benchmark = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const view = useSelector((state) => state.App?.view);
  const [isViewPickerOpen, setIsViewPickerOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const activeTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return getValidTab(params.get("tab"));
  }, [location.search]);
  const activeOption = TAB_OPTIONS.find((option) => option.key === activeTab) || TAB_OPTIONS[0];
  const isH5 = isPC === false || view === "MobileView";

  const handleTabChange = (tab) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", getValidTab(tab));

    navigate({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const handleMobileViewChange = (tab) => {
    handleTabChange(tab);
    setIsViewPickerOpen(false);
    setIsFilterDrawerOpen(false);
  };

  const renderActiveContent = (props = {}) => activeOption.render(props);

  if (isH5) {
    const benchmarkContentProps = activeOption.supportsFilter
      ? {
          isH5,
          filterDrawerOpen: isFilterDrawerOpen,
          onCloseFilterDrawer: () => setIsFilterDrawerOpen(false),
        }
      : { isH5 };

    return (
      <BenchmarkWrapper>
        <div className="benchmark-mobile-switch">
          <div className="benchmark-mobile-switch-row">
            <Button
              className="benchmark-mobile-switch-button"
              onClick={() => setIsViewPickerOpen(true)}
            >
              <span>{activeOption.label}</span>
              <DownOutlined />
            </Button>
            {activeOption.supportsFilter ? (
              <Button
                className="benchmark-mobile-filter-button"
                icon={<FilterOutlined />}
                onClick={() => setIsFilterDrawerOpen(true)}
              >
                Filter
              </Button>
            ) : null}
          </div>
        </div>

        {renderActiveContent(benchmarkContentProps)}

        <ResponsiveModal
          title="Switch Benchmark View"
          open={isViewPickerOpen}
          onCancel={() => setIsViewPickerOpen(false)}
          footer={null}
          height="auto"
          bodyStyle={{ paddingBottom: 16 }}
        >
          <ViewPickerContent>
            {TAB_OPTIONS.map((option) => (
              <button
                type="button"
                key={option.key}
                className={`benchmark-view-option${option.key === activeTab ? " active" : ""}`}
                onClick={() => handleMobileViewChange(option.key)}
              >
                <span>{option.label}</span>
                {option.key === activeTab ? <CheckOutlined /> : null}
              </button>
            ))}
          </ViewPickerContent>
        </ResponsiveModal>
      </BenchmarkWrapper>
    );
  }

  return (
    <BenchmarkWrapper>
      <Tabs
        activeKey={activeTab}
        className="tab_blue_bottom benchmark-tabs"
        animated={false}
        onChange={handleTabChange}
      >
        {TAB_OPTIONS.map((option) => (
          <TabPane tab={option.label} key={option.key}>
            {option.render()}
          </TabPane>
        ))}
      </Tabs>
    </BenchmarkWrapper>
  );
};

export default Benchmark;
